import React, { useEffect, useRef, useState, Fragment } from 'react';
import {
  Box,
  IconButton,
  Modal,
  Tooltip,
  Typography,
  Grid,
  Badge,
  Button,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { parse } from 'json2csv';
import Downloads from 'components/Downloads';
import PropTypes from 'prop-types';
import GetAppIcon from '@mui/icons-material/GetApp';
import Loading from 'components/Loading';
import {
  getManagedTelematicsVehicles,
  getTelematicsData,
} from 'services/util_services';
import { throttle } from 'lodash';

const useStyles = makeStyles()(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  paragraph: {
    padding: 0,
    margin: 0,
    fontSize: 'clamp(16px, 1vw, 2.5rem)',
    fontWeight: 400,
  },
  hr: {
    color: theme.palette.primary.light,
    width: '100%',
  },
  motionDiv: {
    width: '90%',
    maxHeight: '90%',
    overflowY: 'auto',
  },
  scrollContainer: {
    maxHeight: 300,
    overflowY: 'auto',
    scrollBehavior: 'smooth',
  },
}));

const TelematicsModal = ({ open, close }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState({
    registered: false,
    unregistered: false,
  });
  const [telematicsTokens, setTelematicsTokens] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState(null);

  const [data, setData] = useState({
    registered: [],
    unregistered: [],
  });

  const [page, setPage] = useState({
    registered: 0,
    unregistered: 0,
  });

  const [total, setTotal] = useState({
    registered: 0,
    unregistered: 0,
  });

  const scrollRefs = {
    registered: useRef(),
    unregistered: useRef(),
  };

  const fetchData = async (type, pageNo = 0, append = false) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      let allData = [];
      let totalCount = 0;

      for (const entry of telematicsTokens) {
        const t = entry.credentials_token;
        const res = await getManagedTelematicsVehicles({
          credentials_token: t,
          tracking_status: `${type}_tracked`,
          page_no: pageNo,
          per_page: 10,
        });
        const rawData = res?.data?.resource?.resp ?? [];
        totalCount += res?.data?.resource?.total_count || 0;
        const transformedData = rawData.map(item => ({
          telematics_managed_vehicle_name:
            type === 'registered'
              ? item.vehicle_name
              : item.telematics_managed_vehicle_name,
          telematics_managed_regplate:
            type === 'registered'
              ? item.vehicle_id
              : item.telematics_managed_regplate,
        }));
        allData.push(...transformedData);
      }

      // const uniqueData = Array.from(
      //   new Map(
      //     allData.map(item => [item.telematics_managed_regplate, item])
      //   ).values()
      // );

      setData(prev => ({
        ...prev,
        [type]: append ? [...prev[type], ...allData] : allData,
      }));
      setTotal(prev => ({ ...prev, [type]: totalCount }));
      setPage(prev => ({ ...prev, [type]: pageNo }));
    } catch (err) {
      console.error(err);
      setData(prev => ({ ...prev, [type]: [] }));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleScroll = throttle(type => {
    const el = scrollRefs[type]?.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (nearBottom && data[type].length < total[type] && !loading[type]) {
      fetchData(type, page[type] + 1, true);
    }
  }, 500);

 const handleDownload = async fileType => {
  setDownloading(true);
  try {
    const { vehicles } = await fetchAllDataForDownload(downloadType);

    const temp = vehicles.map(item => {
      let y = { ...item };
      // Force name to string with preserved formatting
      const name = item.telematics_managed_vehicle_name;
      y.name = typeof name === 'string' ? name : String(name);
      y.reg_plate = item.telematics_managed_regplate || 'N/A';
      delete y.telematics_managed_vehicle_name;
      delete y.telematics_managed_regplate;
      return y;
    });

    if (fileType === 'xlsx') {
      const sheet = XLSX.utils.json_to_sheet(temp, {
        cellText: false,
        cellDates: true,
      });

      Object.keys(sheet).forEach(cell => {
        if (!cell.startsWith('!')) {
          sheet[cell].t = 's'; // force all cells to string
        }
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, '1');
      XLSX.writeFile(workbook, `${downloadType}_vehicles.xlsx`);
    } else {
      const headers = Object.keys(temp[0]);
      const csv = parse(temp, {
        fields: headers,
        quote: '"', // wrap all fields in quotes to preserve characters
      });

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${downloadType}_vehicles.csv`;
      a.click();
    }
  } catch (error) {
    console.error('Download failed:', error);
  } finally {
    setDownloading(false);
  }
};


  const fetchAllDataForDownload = async type => {
    let allData = [];
    let lastProtocol = null;

    for (const entry of telematicsTokens) {
      const t = entry.credentials_token;
      lastProtocol = entry.protocol;
       const res = await getManagedTelematicsVehicles({
          credentials_token: t,
          tracking_status: `${type}_tracked`,
          per_page: total[type],
        });
        const rawData = res?.data?.resource?.resp ?? [];
        const transformedData = rawData.map(item => ({
          telematics_managed_vehicle_name:
            type === 'registered'
              ? item.vehicle_name
              : item.telematics_managed_vehicle_name,
          telematics_managed_regplate:
            type === 'registered'
              ? item.vehicle_id
              : item.telematics_managed_regplate,
        }));
        allData.push(...transformedData);
    }

    // const uniqueVehicles = Array.from(
    //   new Map(
    //     allData.map(item => [item.telematics_managed_regplate, item])
    //   ).values()
    // );

    // console.log('all vehicle', allData)
    return { protocol: lastProtocol, vehicles: allData };
  };

  useEffect(() => {
    const init = async () => {
      const res1 = await getTelematicsData();
      if (res1?.status === 200 && Array.isArray(res1?.data?.resource)) {
        setTelematicsTokens(res1.data.resource);
      }
    };
    if (open) {
      init();
    }
  }, [open]);

  useEffect(() => {
    if (telematicsTokens.length > 0) {
      fetchData('unregistered');
      fetchData('registered');
    }
    // eslint-disable-next-line
  }, [telematicsTokens]);

  return (
    <>
      <Modal open={open} onClose={() => close(false)} className={classes.modal}>
        <motion.div
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          exit={{ y: 150 }}
          className={classes.motionDiv}
        >
          <Box className={classes.container}>
            <Typography variant='h5' mb={2}>
              {t('telematicsModal.title')}
            </Typography>
            <Grid container spacing={4}>
              {['registered', 'unregistered'].map(type => (
                <Grid item xs={12} md={6} key={type}>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Badge
                      badgeContent={total[type]}
                      color='primary'
                      max={1000}
                    >
                      <Typography variant='h6'>
                        {t(`telematicsModal.${type}Tracked`).toUpperCase()}
                      </Typography>
                    </Badge>
                    {data[type].length > 0 && (
                      <Tooltip title={t(`telematicsModal.${type}DownloadMsg`)}>
                        <IconButton
                          color='primary'
                          size='small'
                          onClick={e => {
                            e.stopPropagation();
                            setDownloadType(type);
                            setOpenDialog(true);
                          }}
                        >
                          {downloading && downloadType === type ? (
                            <CircularProgress size={20} />
                          ) : (
                            <GetAppIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box
                    className={classes.scrollContainer}
                    ref={scrollRefs[type]}
                    onScroll={() => handleScroll(type)}
                  >
                    {loading[type] && <Loading />}
                    {data[type].length > 0 ? (
                      <>
                        {data[type].map((vehicle, idx) => (
                          <Fragment key={`${type}-${idx}`}>
                            <Typography className={classes.paragraph}>
                              Vehicle name:{' '}
                              <span>
                                {vehicle.telematics_managed_vehicle_name}
                              </span>
                            </Typography>
                            <Typography className={classes.paragraph}>
                              Registration Number:{' '}
                              <span>
                                {vehicle.telematics_managed_regplate || 'N/A'}
                              </span>
                            </Typography>
                            <hr className={classes.hr} />
                          </Fragment>
                        ))}
                        {data[type].length < total[type] && (
                          <Button
                            onClick={() =>
                              fetchData(type, page[type] + 1, true)
                            }
                            disabled={loading[type]}
                          >
                            {t('telematicsModal.loadMore')}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Typography>{t('telematicsModal.noVehicle2')}</Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Modal>
      <Downloads
        t={t}
        dialogOpen={openDialog}
        setDialogOpen={setOpenDialog}
        handleDownload={handleDownload}
        downloadType={downloadType}
        downloading={downloading}
      />
    </>
  );
};

TelematicsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

export default TelematicsModal;
